import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Offer } from './OffersContainer';
import { db } from '../../../../../Config/firebase';
import { collection, doc, getDoc, orderBy, query, onSnapshot, updateDoc, where } from 'firebase/firestore';
import { Box, Checkbox, Chip, Paper, Tooltip, Typography } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { format } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAppSelector, useAppDispatch } from '../../../../../state/state';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { FLAVOR } from "../../../../../Config/firebase"
import { openModal } from '../../../../../state/ModalSlice/ModalSlice';
import { useTheme } from '@mui/material/styles';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useDispatch } from 'react-redux';
import { setFieldsForOfferQRCode, setFieldsForQRCodeSource} from '../../../../../../src/state/ArbitrarySlice/ArbitratySlice';
import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2';


type ticketType = {
  quantity: number;
  ticketPrice: number;
  ticketType: string;
}

type offerPurchasers = {
  id: string;
  payeeName: string;
  payeeNumber: string;
  pdfUrl: string;
  tickets: ticketType[];
  isUsed: boolean;
  emailId: string;
}

export default function OfferDetailPage() {
  const { offerId } = useParams()
  const [offer, setOffer] = useState<Offer | null>(null);
  const [purchasers, setPurchasers] = useState<offerPurchasers[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [presentCount, setPresentCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const providerId = useAppSelector(selectEffectiveUID);
  const dispatch = useDispatch();

  const unsubscribeRef = useRef<() => void | undefined>();

  const theme = useTheme();

  const handleAction = () => {
    dispatch(openModal("QREventsandOfferModalOpen"));
    dispatch(setFieldsForQRCodeSource({ qrCodeSource: "offer" }));
    dispatch(setFieldsForOfferQRCode({
        BuzzName: offer?.BuzzName || '',
        StartDate: offer?.StartDate ? {
            seconds: offer.StartDate.seconds,
            nanoseconds: offer.StartDate.nanoseconds
        } : { seconds: 0, nanoseconds: 0 },
        EndDate: offer?.EndDate ? {
            seconds: offer.EndDate.seconds,
            nanoseconds: offer.EndDate.nanoseconds
        } : { seconds: 0, nanoseconds: 0 },
        address: offer?.geoLocation?.address || '',
        offerLink: offer?.offerLink || '',
    }));
    
  }
  useEffect(() => {
    const fetchOfferDetails = async () => {
      if (!offerId || !providerId) return;

      try {
        setIsLoading(true);
        const offerDoc = await getDoc(doc(db, `/ServiceProviders/${providerId}/Offers/`, offerId));
        if (!offerDoc.exists()) {
          console.error("Offer not found");
          setIsLoading(false);
          return;
        }

        setOffer({ id: offerDoc.id, ...offerDoc.data() } as Offer);

        const offerConsumersRef = collection(db, 'ServiceProviders', providerId, 'OfferConsumers');
        const q = query(
          offerConsumersRef,
          where('offerId', '==', offerId),
          orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const offerConsumersList: offerPurchasers[] = [];
          snapshot.forEach((doc: any) => {
            const docData = doc.data()
            offerConsumersList.push({
              id: doc.id, payeeName: docData.payeeName,
              payeeNumber: docData.payeeNumber,
              pdfUrl: docData.pdfUrl,
              tickets: docData.tickets ? docData.tickets : [],
              isUsed: docData.isUsed,
              emailId: docData.emailId,
              participants: docData.participants||0
            } as offerPurchasers);
          });
          setPurchasers(offerConsumersList);
          setIsLoading(false);
        }, (error) => {
          console.error("Error fetching offers: ", error);
          setIsLoading(false);
        });

        // Store the unsubscribe function
        unsubscribeRef.current = unsubscribe;

        // Cleanup function
        return () => {
          if (unsubscribeRef.current) {
            unsubscribeRef.current();
          }
        };
      } catch (error) {
        console.error("Error fetching offer details:", error);
        setIsLoading(false);
      }
    };
    fetchOfferDetails();
  }, [offerId, providerId]);


  useEffect(() => {
    const totalRevenue = purchasers.reduce((sum, purchaser) => {
      return sum + purchaser.tickets.reduce((ticketSum, ticket) => {
        return ticketSum + ticket.ticketPrice * ticket.quantity;
      }, 0);
    }, 0);
    setTotalRevenue(totalRevenue);

    const presentCount = purchasers.filter(purchaser => purchaser.isUsed).length;
    setPresentCount(presentCount);
  }, [purchasers])

  const handleAttendanceChange = async (purchaserId: string, isUsed: boolean) => {
    const offerConsumersRef = collection(db, 'ServiceProviders', providerId, 'OfferConsumers');
    const docRef = doc(offerConsumersRef, purchaserId);
    const updateData = {
      isUsed: isUsed,
      ...(isUsed && { availedTime: new Date() })
    };
    await updateDoc(docRef, updateData);
  };

  const filteredPurchasers = purchasers.filter(purchaser =>
    purchaser.payeeName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cc_brand border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 font-medium">Loading offer details...</p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="text-center">
            <p className="text-xl text-gray-800 font-medium">Offer Not Found</p>
            <p className="text-gray-600 mt-2">The offer you're looking for doesn't exist or has been removed.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-[1px] border-t-cc_brand border-t-4 border-gray-300 h-[100%] w-[100%] bg-white shadow-xl flex gap-2 p-2 rounded-xl"
      style={{ boxShadow: "0 0px 6px 0px rgba(0, 0, 0, 0.1), 0 0px 6px 0px rgba(0, 0, 0, 0.06)" }}>

      {/* Left Section */}
      <div className="box-left h-full w-[40%] bg-white shadow-xl border-r-[1px] border-cc_modal-grey-border rounded-xl">
        {/* Event Header with Gradient Overlay */}
        <div className="relative h-64 rounded-t-xl overflow-hidden">
          <img
            src={offer.url}
            alt={offer.BuzzName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex justify-between items-center mb-2">
              <h1 className="text-2xl font-bold tracking-tight">{offer.BuzzName}</h1>

            </div>

            {/* Event Meta Information */}
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2">
                <CalendarTodayIcon fontSize="small" />
                <span>valid untill : {format(new Date(offer.EndDate?.seconds * 1000), 'MMM dd, yyyy')}</span>
              </div>
             
              <div className="flex justify-between items-center gap-2">
                <div>
                  <LocationOnIcon fontSize="small" />
                  <span className="truncate max-w-[75%] overflow-hidden text-ellipsis">{offer.placeName}</span>
                </div>
                <div className=" ">
                  <Box sx={{ display: "flex", gap: 2, mt: 1, position: 'absolute', top: -100, right: 16 }}>
                    <Tooltip title="Get your QR Code" arrow>
                      <Box
                        onClick={() => handleAction()}
                        sx={{
                          cursor: "pointer",
                          bgcolor: theme.palette.background.paper,
                          color: "Black",
                          py: 0.5,
                          px: 2,
                          borderRadius: 20,
                          display: "flex",
                          alignItems: "center",
                          backdropFilter: 'blur(4px)', // Apply backdrop blur
                          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)', // Optional: add shadow for depth
                          transition: "all 0.3s",
                          "&:hover": {
                            bgcolor: theme.palette.secondary.main,
                            transform: "scale(1.03)",
                          },
                        }}
                      >
                        <QrCode2Icon sx={{ mr: 1, fontSize: 18 }} />
                        <Typography variant="button" sx={{ fontSize: 13 }}>
                          Get QR Code
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </div>

                <div className="bg-white/20 backdrop-blur-sm px-2 py-1.5 rounded-full cursor-pointer" onClick={() => {

                  if(FLAVOR==="DEVELOPMENT"){
                  window.open(`https://plundrdev.hosaksham.com/details/${providerId}/offer/${offerId}`, '_blank');
                }
                else if(FLAVOR==="PRODUCTION"){
                  window.open(`https://www.plundr.besaksham.com/details/${providerId}/offer/${offerId}`, '_blank');
                }
                }}>
                  <span className="text-sm font-semibold">View Event Page <ArrowOutwardIcon fontSize="small" /></span>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="p-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl">
              <div className="flex flex-col items-center">
                <div className="bg-blue-500/10 p-2 rounded-full mb-2">
                  <CurrencyRupeeIcon className="text-blue-600" />
                </div>
                <p className="text-lg font-bold text-blue-600">₹{totalRevenue}</p>
                <p className="text-sm text-blue-600/70">Revenue</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-2xl">
              <div className="flex flex-col items-center">
                <div className="bg-green-500/10 p-2 rounded-full mb-2">
                  <PeopleAltIcon className="text-green-600" />
                </div>
                <p className="text-lg font-bold text-green-600">{purchasers.length}</p>
                <p className="text-sm text-green-600/70">Total Purchasers</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl">
              <div className="flex flex-col items-center">
                <div className="bg-purple-500/10 p-2 rounded-full mb-2">
                  <CheckCircleIcon className="text-purple-600" />
                </div>
                <p className="text-lg font-bold text-purple-600">{presentCount}</p>
                <p className="text-sm text-purple-600/70">Redeemed</p>
              </div>
            </div>
          </div>

          {/* Event Description */}
          <div className="bg-gray-50 p-6 rounded-2xl">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">About Event</h3>
            <p className="text-gray-600 leading-relaxed">{offer.Description}</p>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="w-[60%] h-full">
        <div className="p-6">
          {/* Header with Search */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Participants</h2>
              <p className="text-sm text-gray-500 mt-1">
                {purchasers.length} purchasers
              </p>
            </div>
            <div className="relative w-72">
              <input
                type="text"
                placeholder="Search participants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2.5 pl-11 rounded-xl border border-gray-200 focus:outline-none focus:border-cc_brand focus:ring-1 focus:ring-cc_brand text-sm transition-all duration-200"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
          </div>

          {/* Participants List */}
          <div className="space-y-3 max-h-[calc(100vh-220px)] overflow-y-auto diff-scrollbar pr-2">
            {filteredPurchasers.length > 0 ? (
              filteredPurchasers.map((purchaser) => (
                <div
                  key={purchaser.id}
                  className="group bg-white p-2.5 border-[1px]  rounded-xl  border-gray-300 hover:shadow-lg hover:border-cc_brand/30 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
                        <PersonOutlineIcon className="text-gray-700" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 group-hover:text-cc_brand transition-colors duration-300">
                          {purchaser.payeeName}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-gray-500">{purchaser.payeeNumber}</span>
                          {purchaser.emailId && <span className="w-1 h-1 bg-gray-300 rounded-full"></span>}
                          <span className="text-sm text-gray-500">{purchaser.emailId}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {purchaser.tickets.map((ticket) => {
                        return (<Chip
                          key={ticket.ticketType}
                          label={`${ticket.ticketType} - ${ticket.quantity}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          className="bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                        />)
                      })}

                      <Tooltip title="Mark Redeemed">
                        <div className="relative">
                          <Checkbox
                            checked={purchaser.isUsed}
                            onChange={(e) => handleAttendanceChange(purchaser.id, e.target.checked)}
                            color="success"
                            className="rounded-full transition-transform duration-200 hover:scale-110"
                          />
                          {purchaser.isUsed && (
                            <div className="absolute -top-2 -right-2 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                          )}
                        </div></Tooltip>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50 rounded-2xl">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                  strokeLinejoin="round" className="text-gray-400 mb-4">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <p className="text-lg font-medium text-gray-600 mb-2">No Participants Yet</p>
                <p className="text-sm text-gray-500 text-center">
                  Start adding participants to track attendance and manage your event.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
